// SMS Service for Frontend
export interface SMSMessage {
  id: number;
  to: string;
  from: string;
  message: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  created_at: string;
  sent_at?: string;
  delivered_at?: string;
  error?: string;
  cost: number;
  template_id?: string;
}

export interface SMSTemplate {
  id: string;
  name: string;
  content: string;
  variables: string[];
  category: string;
  language: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
}

export interface SMSUsage {
  total_sent: number;
  total_delivered: number;
  total_failed: number;
  total_cost: number;
  period: {
    start: string;
    end: string;
  };
}

export interface SendSMSRequest {
  to: string;
  message?: string;
  template_id?: string;
  variables?: Record<string, string>;
}

export interface SendBulkSMSRequest {
  recipients: string[];
  message?: string;
  template_id?: string;
  variables?: Record<string, string>;
}

export interface SendNotificationSMSRequest {
  user_id: number;
  type: 'welcome' | 'login_alert' | 'payment_confirmation' | 'invoice_reminder' | 'weather_alert' | 'market_price_alert' | 'agricultural_tip' | 'system_maintenance';
  variables?: Record<string, string>;
  phone_number?: string;
}

export interface BulkSMSResponse {
  total_sent: number;
  total_failed: number;
  messages: SMSMessage[];
  errors: string[];
}

export interface SMSHistoryResponse {
  messages: SMSMessage[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

class SMSService {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/sms') {
    this.baseUrl = baseUrl;
  }

  /**
   * Send a single SMS message
   */
  async sendSMS(request: SendSMSRequest): Promise<SMSMessage> {
    const response = await fetch(`${this.baseUrl}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to send SMS: ${error}`);
    }

    return response.json();
  }

  /**
   * Send SMS to multiple recipients
   */
  async sendBulkSMS(request: SendBulkSMSRequest): Promise<BulkSMSResponse> {
    const response = await fetch(`${this.baseUrl}/send/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to send bulk SMS: ${error}`);
    }

    return response.json();
  }

  /**
   * Send notification SMS using predefined templates
   */
  async sendNotificationSMS(request: SendNotificationSMSRequest): Promise<SMSMessage> {
    const response = await fetch(`${this.baseUrl}/send/notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to send notification SMS: ${error}`);
    }

    return response.json();
  }

  /**
   * Get available SMS templates
   */
  async getTemplates(category?: string): Promise<SMSTemplate[]> {
    const url = category ? `${this.baseUrl}/templates?category=${category}` : `${this.baseUrl}/templates`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get SMS templates: ${error}`);
    }

    return response.json();
  }

  /**
   * Get a specific SMS template
   */
  async getTemplate(templateId: string): Promise<SMSTemplate> {
    const response = await fetch(`${this.baseUrl}/templates/${templateId}`, {
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get SMS template: ${error}`);
    }

    return response.json();
  }

  /**
   * Get SMS usage statistics
   */
  async getUsage(startDate?: string, endDate?: string): Promise<SMSUsage> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const url = `${this.baseUrl}/usage?${params.toString()}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get SMS usage: ${error}`);
    }

    return response.json();
  }

  /**
   * Get SMS message history
   */
  async getHistory(limit?: number, offset?: number): Promise<SMSHistoryResponse> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    const url = `${this.baseUrl}/history?${params.toString()}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get SMS history: ${error}`);
    }

    return response.json();
  }

  /**
   * Process template with variables
   */
  processTemplate(template: SMSTemplate, variables: Record<string, string>): string {
    let content = template.content;
    
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{{${key}}}`;
      content = content.replace(new RegExp(placeholder, 'g'), value);
    }
    
    return content;
  }

  /**
   * Validate phone number format
   */
  validatePhoneNumber(phoneNumber: string): boolean {
    // Remove any non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if it's a valid Nigerian phone number
    if (cleaned.length < 10 || cleaned.length > 13) {
      return false;
    }
    
    // Check for Nigerian country codes
    if (cleaned.startsWith('234')) {
      return cleaned.length === 13;
    } else if (cleaned.startsWith('0')) {
      return cleaned.length === 11;
    }
    
    return false;
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    if (cleaned.startsWith('234')) {
      return `+${cleaned}`;
    } else if (cleaned.startsWith('0')) {
      return `+234${cleaned.substring(1)}`;
    }
    
    return phoneNumber;
  }

  /**
   * Get SMS cost estimate
   */
  getCostEstimate(messageLength: number, recipientCount: number = 1): number {
    // Basic cost calculation (this would typically come from provider)
    const costPerSMS = 0.05; // $0.05 per SMS
    const smsCount = Math.ceil(messageLength / 160); // SMS character limit
    return smsCount * recipientCount * costPerSMS;
  }

  /**
   * Get notification types with descriptions
   */
  getNotificationTypes(): Array<{value: string, label: string, description: string}> {
    return [
      {
        value: 'welcome',
        label: 'Welcome Message',
        description: 'Sent when a new user registers'
      },
      {
        value: 'login_alert',
        label: 'Login Alert',
        description: 'Security notification for account access'
      },
      {
        value: 'payment_confirmation',
        label: 'Payment Confirmation',
        description: 'Confirmation of successful payment'
      },
      {
        value: 'invoice_reminder',
        label: 'Invoice Reminder',
        description: 'Reminder for outstanding invoices'
      },
      {
        value: 'weather_alert',
        label: 'Weather Alert',
        description: 'Important weather warnings'
      },
      {
        value: 'market_price_alert',
        label: 'Market Price Alert',
        description: 'Significant changes in crop prices'
      },
      {
        value: 'agricultural_tip',
        label: 'Agricultural Tip',
        description: 'Helpful farming tips and advice'
      },
      {
        value: 'system_maintenance',
        label: 'System Maintenance',
        description: 'Scheduled maintenance notifications'
      }
    ];
  }

  /**
   * Get template categories
   */
  getTemplateCategories(): Array<{value: string, label: string}> {
    return [
      { value: 'account', label: 'Account' },
      { value: 'security', label: 'Security' },
      { value: 'payment', label: 'Payment' },
      { value: 'invoice', label: 'Invoice' },
      { value: 'weather', label: 'Weather' },
      { value: 'market', label: 'Market' },
      { value: 'tips', label: 'Agricultural Tips' },
      { value: 'system', label: 'System' }
    ];
  }

  /**
   * Get SMS status colors for UI
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'sent':
        return 'text-blue-600 bg-blue-100';
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  /**
   * Get SMS status label
   */
  getStatusLabel(status: string): string {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string {
    return localStorage.getItem('authToken') || '';
  }
}

// Export singleton instance
export const smsService = new SMSService();

// Export class for custom instances
export default SMSService;
